import { apiClient, ApiClient } from './client';
import type { 
  ScenarioDetailResponse, 
  ScenarioMessage, 
  GenerateScenarioMessagePayload, 
  AddScenarioMessagePayload,
  ScenarioMemberType,
  CreateScenarioPayload,
  ScenarioListResponse,
  ScenarioPersona,
  ScenarioCharacter,
  ScenarioDefinition,
  ScenarioNextSpeaker,
  TimelineEventsPayload
} from './types/scenario';

class Scenario {
  public readonly id: string;
  private readonly apiClient: ApiClient;
  public persona: ScenarioPersona;
  public characters: ScenarioCharacter[];
  public messages: ScenarioMessage[];
  public definition: ScenarioDefinition;
  public available_characters: ScenarioCharacter[];
  public next_speaker: ScenarioNextSpeaker | null;
  constructor(id: string, apiClient: ApiClient, details: ScenarioDetailResponse) {
    this.id = id;
    this.apiClient = apiClient;
    this.definition = details.definition;
    this.persona = details.persona;
    this.characters = details.characters;
    this.available_characters = details.available_characters || []; // only populated in getAvailableCharacters case
    this.messages = details.messages;
    this.next_speaker = details.next_speaker || null; // only populated in getNextSpeaker case
  }

  async get(): Promise<Scenario> {
    try {
      const response = await this.apiClient.get(`/scenarios/${this.id}/`);
      return new Scenario(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to fetch scenario details:', error);
      throw error;
    }
  }

  async delete(): Promise<void> {
    try {
      await this.apiClient.delete(`/scenarios/${this.id}/`);
    } catch (error) {
      console.error('Failed to delete scenario:', error);
      throw error;
    }
  }

  async addMessage(content: string): Promise<Scenario> {
    try {
      const requestBody: AddScenarioMessagePayload = { content };
      const response = await this.apiClient.post(`/scenarios/${this.id}/add-message/`, requestBody);
      return new Scenario(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to add message:', error);
      throw error;
    }
  }

  async generate(member_type: ScenarioMemberType, character?: string): Promise<Scenario> {
    try {
      const requestBody: GenerateScenarioMessagePayload = { member_type };
      if (character) {
        requestBody.character = character;
      }
      const response = await this.apiClient.post(`/scenarios/${this.id}/generate/`, requestBody);
      return new Scenario(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to generate message:', error);
      throw error;
    }
  }

  async rewindToMessage(messageId: string): Promise<Scenario> {
    try {
      const response = await this.apiClient.post(`/scenarios/${this.id}/rewind-to-message/`, { message: messageId });
      return new Scenario(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to rewind scenario:', error);
      throw error;
    }
  }

  async bump(): Promise<void> {
    try {
      await this.apiClient.post(`/scenarios/${this.id}/bump/`);
    } catch (error) {
      console.error('Failed to bump scenario:', error);
      throw error;
    }
  }

  async newScenarioFromMessage(messageId: string): Promise<Scenario> {
    try {
      const response = await this.apiClient.post(`/scenarios/${this.id}/new-scenario-from-message/`, { message: messageId });
      return new Scenario(response.data.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to create new scenario from message:', error);
      throw error;
    }
  }

  async editMessage(messageId: string, content: string): Promise<Scenario> {
    try {
      const response = await this.apiClient.post(`/scenarios/${this.id}/edit-message/`, {
        message: messageId,
        content
      });
      return new Scenario(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to edit message:', error);
      throw error;
    }
  }

  async regenerate(messageId: string): Promise<Scenario> {
    try {
      const response = await this.apiClient.post(`/scenarios/${this.id}/regenerate/`, { message: messageId });
      return new Scenario(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to regenerate message:', error);
      throw error;
    }
  }

  async addCharacter(characterId: string): Promise<Scenario> {
    try {
      const response = await this.apiClient.post(`/scenarios/${this.id}/add-character/`, { character: characterId });
      return new Scenario(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to add character:', error);
      throw error;
    }
  }

  async removeCharacter(characterId: string): Promise<Scenario> {
    try {
      const response = await this.apiClient.post(`/scenarios/${this.id}/remove-character/`, { character: characterId });
      return new Scenario(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to remove character:', error);
      throw error;
    }
  }

  async addAvailableCharacter(characterId: string): Promise<Scenario> {
    try {
      const response = await this.apiClient.post(`/scenarios/${this.id}/add-available-character/`, { character: characterId });
      return new Scenario(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to add available character:', error);
      throw error;
    }
  }

  async removeAvailableCharacter(characterId: string): Promise<Scenario> {
    try {
      const response = await this.apiClient.post(`/scenarios/${this.id}/remove-available-character/`, { character: characterId });
      return new Scenario(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to remove available character:', error);
      throw error;
    }
  }
  //mutable method due to backend not returning a scenario object
  async getAvailableCharacters(): Promise<Scenario> {
    try {
      const response = await this.apiClient.get(`/scenarios/${this.id}/available-characters/`);
      this.available_characters = response.data.available_characters;
      return this;
    } catch (error) {
      console.error('Failed to get available characters:', error);
      throw error;
    }
  }

  //mutable method due to backend not returning a scenario object
  async getNextSpeaker(): Promise<Scenario> {
    try {
      const response = await this.apiClient.get(`/scenarios/${this.id}/next-speaker/`);
      this.next_speaker = response.data.next_speaker;
      return this;
    } catch (error) {
      console.error('Failed to get next speaker:', error);
      throw error;
    }
  }

  async setPersona(persona: string): Promise<Scenario> {
    try {
      const response = await this.apiClient.post(`/scenarios/${this.id}/set-persona/`, { persona });
      return new Scenario(this.id, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to set persona:', error);
      throw error;
    }
  }

}

export const scenarios = {
  async list(): Promise<ScenarioListResponse[]> {
    try {
      const response = await apiClient.get('/scenarios/');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch scenarios:', error);
      throw error;
    }
  },

  async get(scenarioId: string): Promise<Scenario> {
    try {
      const response = await apiClient.get(`/scenarios/${scenarioId}/`);
      return new Scenario(scenarioId, apiClient, response.data);
    } catch (error) {
      console.error('Failed to fetch scenario details:', error);
      throw error;
    }
  },

  async create(definitionOrPayload: string | CreateScenarioPayload, persona?: string): Promise<Scenario> {
    try {
      let payload: CreateScenarioPayload;
      
      if (typeof definitionOrPayload === 'string' && persona) {
        // Old format: definition and persona as separate parameters
        payload = { definition: definitionOrPayload, persona };
      } else if (typeof definitionOrPayload === 'object') {
        // New format: full payload object
        payload = definitionOrPayload;
      } else {
        throw new Error('Invalid arguments to create scenario');
      }
      
      const response = await apiClient.post('/scenarios/', payload);
      return new Scenario(response.data.id, apiClient, response.data);
    } catch (error) {
      console.error('Failed to create scenario:', error);
      throw error;
    }
  }
};

export type { Scenario as ScenarioType };